/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import { handleRef, Ref } from '@fluentui/react-component-ref';
import { Placement } from '@popperjs/core';
import classNames from 'classnames';
import React, {
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import ReactIs from 'react-is';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { Box, BoxProps, Button, ButtonProps } from '../../atoms';
import {
    useAutoControlled,
    useKeyEventListener,
    useOutsideClickListener,
} from '../../hooks';
import * as utils from '../../utils';

export type Position =
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'left-top'
    | 'left-center'
    | 'left-bottom'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right'
    | 'right-bottom'
    | 'right-center'
    | 'right-top';

export interface PopoverProps
    extends React.HTMLAttributes<HTMLDivElement>,
        React.RefAttributes<HTMLDivElement> {
    open?: boolean;
    defaultOpen?: boolean;
    onTriggerClick?(ev: React.MouseEvent<HTMLElement, MouseEvent>): void;
    trigger?: React.ReactElement;
    headline?: string | React.ReactElement;
    paragraph?: string | React.ReactElement;
    closeButton?: boolean | React.ReactElement<ButtonProps> | ButtonProps;
    onCloseButtonClick?(ev: React.MouseEvent<HTMLElement, MouseEvent>): void;
    closeKey?: string;
    onCloseKeyPressed?(ev: KeyboardEvent): void;
    onOutsideClick?(ev: MouseEvent): void;
    position?: Position;
    borderOffset?: number;
    triggerOffset?: number;
    strategy?: 'fixed' | 'absolute';
    isPopoverArrowMissing?: boolean;
    content?: ReactNode;
    children?: ReactNode;
    boxProps?: BoxProps;
}

/**
 * @name      m-popover
 * @component-type      molecule
 * @copyright Robert Bosch GmbH
 * @description
 * A popover is a larger variant of the tooltip. It can optionally have a button, a close button, or a headline, and one of 12 arrow positions.
 */

export const Popover = React.forwardRef<HTMLDivElement, PopoverProps>(
    PopoverInner,
);

const positionToPlacement: Record<Position, Placement> = {
    'top-left': 'top-start',
    'top-center': 'top',
    'top-right': 'top-end',
    'right-top': 'right-start',
    'right-center': 'right',
    'right-bottom': 'right-end',
    'bottom-right': 'bottom-end',
    'bottom-center': 'bottom',
    'bottom-left': 'bottom-start',
    'left-top': 'left-start',
    'left-center': 'left',
    'left-bottom': 'left-end',
};

const positionToInversePosition: Record<Position, Position> = {
    'top-left': 'bottom-left',
    'top-center': 'bottom-center',
    'top-right': 'bottom-right',
    'bottom-center': 'top-center',
    'bottom-left': 'top-left',
    'bottom-right': 'top-right',
    'left-bottom': 'right-bottom',
    'left-center': 'right-center',
    'left-top': 'right-top',
    'right-bottom': 'left-bottom',
    'right-center': 'left-center',
    'right-top': 'left-top',
};

const mapPositionToPlacement = (pos: Position): Placement => {
    return positionToPlacement[pos] || 'bottom';
};

const mapPositionToArrow = (pos: Position): Position => {
    return positionToInversePosition[pos] || 'top-center';
};

type PopperContainerProps = {
    open: boolean;
};
const PopperContainer = styled.div`
    visibility: ${({ open }: PopperContainerProps) =>
        open ? 'visible' : 'hidden'};
    pointer-events: ${({ open }: PopperContainerProps) =>
        open ? 'auto' : 'hidden'};
    display: block;
    position: absolute;
    z-index: 99;
`;

function PopoverInner(
    props: PopoverProps,
    forwardedRef: React.ForwardRefExoticComponent<HTMLDivElement>,
) {
    const {
        headline,
        paragraph,
        closeButton = false,
        position = 'bottom-center',
        borderOffset = 0,
        triggerOffset = 16,
        strategy = 'absolute',
        onOutsideClick,
        onTriggerClick,
        onCloseKeyPressed,
        onCloseButtonClick,
        trigger,
        closeKey,
        isPopoverArrowMissing,
        content,
        children,
        boxProps = {},
        defaultOpen,
        open: parentOpen,
        ...rest
    } = props;

    const [open, setOpen] = useAutoControlled({
        initialValue: false,
        defaultValue: defaultOpen,
        value: parentOpen,
    });
    const triggerRef = useRef<HTMLElement>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement>(null);
    const { styles, attributes, update } = usePopper(
        triggerRef.current,
        popperElement,
        {
            placement: mapPositionToPlacement(position),
            strategy,
            modifiers: [
                {
                    name: 'flip',
                    enabled: false,
                },
                {
                    name: 'offset',
                    options: {
                        offset: [borderOffset, triggerOffset],
                    },
                },
            ],
        },
    );

    useEffect(() => {
        if (popperElement && update) {
            const observer = new ResizeObserver(update);
            observer.observe(popperElement);
            return () => {
                observer.unobserve(popperElement);
            };
        }
    }, [popperElement, update]);

    const handleCloseKeyPressed = (ev: KeyboardEvent) => {
        if (typeof onCloseKeyPressed === 'function') {
            onCloseKeyPressed(ev);
        }
        setOpen(false);
    };

    const handleDocumentClick = (ev: MouseEvent) => {
        if (typeof onOutsideClick === 'function') {
            onOutsideClick(ev);
        }
        setOpen(false);
    };

    useOutsideClickListener({
        onOutsideClick: handleDocumentClick,
        elements: [popperElement, triggerRef.current],
        enabled: open,
    });

    useKeyEventListener({
        onKeyPressed: handleCloseKeyPressed,
        key: closeKey,
        enabled: open,
    });

    function handleTriggerClick(ev: React.MouseEvent<HTMLElement>) {
        if (typeof onTriggerClick === 'function') {
            onTriggerClick(ev);
        }
        utils.invoke(trigger, 'props.onClick', ev);
        setOpen(isOpen => !isOpen);
    }

    const renderTrigger = (): JSX.Element | undefined => {
        return (
            trigger && (
                <Ref innerRef={triggerRef}>
                    {React.cloneElement(trigger, {
                        ...trigger.props,
                        onClick: handleTriggerClick,
                    })}
                </Ref>
            )
        );
    };

    const renderCloseButton = (): JSX.Element | undefined => {
        const handleOnClick = (
            ev: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        ) => {
            if (typeof onCloseButtonClick === 'function') {
                onCloseButtonClick(ev);
            }
            utils.invoke(closeButton, 'onClick', ev);
            setOpen(false);
        };

        if (closeButton === true) {
            return (
                <Button
                    action="close"
                    mode="integrated"
                    icon={{
                        iconName: 'close',
                        isUiIcon: true,
                    }}
                    onClick={handleOnClick}
                    aria-label="close popover"
                />
            );
        } else if (ReactIs.isElement(closeButton)) {
            return React.cloneElement(closeButton, {
                ...closeButton.props,
                onClick: handleOnClick,
            });
        } else if (typeof closeButton === 'object') {
            return (
                <Button
                    action="close"
                    mode="integrated"
                    icon="close"
                    onClick={handleOnClick}
                    aria-label="close popover"
                    {...closeButton}
                />
            );
        }
        return null;
    };

    const handlePopperRef = useCallback(
        (el: HTMLDivElement) => {
            setPopperElement(el);
            handleRef(forwardedRef, el);
        },
        [forwardedRef],
    );

    const arrowPosition = mapPositionToArrow(position);

    const popoverClasses = classNames([
        'm-popover',
        { [`-${arrowPosition}`]: position },
        { '-close-button': closeButton },
        { '-without-arrow': isPopoverArrowMissing },
    ]);

    return (
        <>
            {renderTrigger()}
            <PopperContainer
                className={popoverClasses}
                ref={handlePopperRef}
                style={{ ...styles.popper, ...props.style }}
                {...attributes.popper}
                {...rest}
                open={open}
            >
                <Box {...boxProps}>
                    <div className="m-popover__content">
                        {(headline || closeButton) && (
                            <div className="m-popover__head">
                                {headline}
                                {renderCloseButton()}
                            </div>
                        )}
                        {paragraph && (
                            <div className="m-popover__paragraph">
                                {paragraph}
                            </div>
                        )}
                        {content || children}
                    </div>
                </Box>
            </PopperContainer>
        </>
    );
}
